import {
  BulkOperationStatus,
  BulkSendJobsChannel_Read,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { noop } from 'lodash-es';
import { createContext, useCallback, useMemo } from 'react';

import useBulkOperationProgress from '~hooks/useBulkOperationProgress/useBulkOperationProgress';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';

type BulkSendProgressContextType = {
  enqueueBulkSend: (sendId: string, jobIds: string[]) => void;
};

const BulkSendProgressContext = createContext<BulkSendProgressContextType>({
  enqueueBulkSend: noop,
});

function bulkSendStatusText(progress: BulkSendJobsChannel_Read) {
  if (progress.status === BulkOperationStatus.ERROR) {
    return t('common.bulk_send_progress.stopped');
  }

  if (progress.status !== BulkOperationStatus.FINISHED) {
    return t('common.bulk_send_progress.sending', {
      count: progress.count,
      processed: progress.succeeded_count + progress.failed_count,
    });
  }

  if (progress.failed_count > 0) {
    return t('common.bulk_send_progress.failed', { count: progress.failed_count });
  }

  return t('common.bulk_send_progress.sent', { count: progress.succeeded_count });
}

function BulkSendProgressContextProvider({ children }: React.PropsWithChildren) {
  const { ordersDispatchStore } = useStores();

  // Sent jobs leave the overlay through their own job updates; failed ones never get one.
  const clearFailedJobsWaitingForRTU = useCallback(
    (progress: BulkSendJobsChannel_Read) => {
      const failedJobIds = progress.failed.map((item) => item.id);
      const failedFriendlyJobIds = ordersDispatchStore.jobsWaitingForRTU
        .filter((entry) => failedJobIds.includes(entry.jobId))
        .map((entry) => entry.friendlyJobId);
      ordersDispatchStore.clearJobsWaitingForRTU(failedFriendlyJobIds);
    },
    [ordersDispatchStore],
  );

  const { progressStack, track } = useBulkOperationProgress<BulkSendJobsChannel_Read>({
    channel: realTimeChannels.BulkSendJobsChannel,
    onSettled: clearFailedJobsWaitingForRTU,
    statusText: bulkSendStatusText,
    title: t('common.bulk_send_progress.title'),
  });

  const enqueueBulkSend = useCallback(
    (sendId: string, jobIds: string[]) => {
      ordersDispatchStore.setJobsWaitingForRTU(jobIds);
      track(sendId, jobIds.length);
    },
    [ordersDispatchStore, track],
  );

  const contextValue = useMemo(() => ({ enqueueBulkSend }), [enqueueBulkSend]);

  return (
    <BulkSendProgressContext.Provider value={contextValue}>
      {children}
      {progressStack}
    </BulkSendProgressContext.Provider>
  );
}

export { BulkSendProgressContextProvider };
export default BulkSendProgressContext;
